import React, { useRef, useEffect, type RefObject } from 'react';
import { components, type OptionProps, type OptionType } from '@atlaskit/select';
import { fg } from '@atlassian/jira-feature-gating';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { scrollIntoView } from './utils.tsx';
import { STICKY_FOOTER_CLASS_NAME } from './constant.tsx';
import { useMenuListRef } from './MenuListWithAutoKeyboardScroll.tsx';

export const OptionWithAutoKeyboardScroll = <T = OptionType, IsMulti extends boolean = false>({
	children,
	containerRef,
	...optionProps
}: {
	/**
	 * @deprecated
	 * Use OptionWithAutoKeyboardScroll together with MenuListWithAutoKeyboardScroll.
	 * This avoids the need to recreate the option component during each render phase.
	 * https://hello.atlassian.net/wiki/spaces/Spork/pages/5931717699/RFC+-+Depreciating+containerRef+prop+in+OptionWithAutoKeyboardScroll
	 */
	containerRef?: RefObject<HTMLDivElement>;
} & OptionProps<T, IsMulti>) => {
	const focusedOptionRef = useRef<HTMLElement>();
	let containerRefFromContext: RefObject<HTMLDivElement> | undefined;

	if (fg('depreciate-select-with-footer-options-containerref')) {
		// eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
		containerRefFromContext = useMenuListRef();
	}

	useEffect(() => {
		if (optionProps.isFocused) {
			// We only auto scroll for focus with keyboard only, not for focus with mouse hover
			// because mouse users will manual scroll when browsing options
			if (!focusedOptionRef.current?.parentElement || focusedOptionRef.current?.matches(':hover')) {
				return;
			}

			// We need to add bottom offset when scrolling which is the current height of sticky footer options group
			const stickFooterBottomOffset =
				globalThis?.document.querySelector(`.${STICKY_FOOTER_CLASS_NAME}`)?.clientHeight ?? 0;

			if (fg('depreciate-select-with-footer-options-containerref')) {
				scrollIntoView(
					containerRefFromContext?.current ??
						containerRef?.current ??
						focusedOptionRef.current.parentElement,
					focusedOptionRef.current,
					stickFooterBottomOffset,
				);
			} else {
				scrollIntoView(
					containerRef?.current ?? focusedOptionRef.current.parentElement,
					focusedOptionRef.current,
					stickFooterBottomOffset,
				);
			}
		}
	}, [containerRefFromContext, containerRef, optionProps.isFocused]);

	return (
		<components.Option
			{...optionProps}
			innerRef={mergeRefs(optionProps.innerRef, focusedOptionRef)}
		>
			{children}
		</components.Option>
	);
};
